"use client"

import { Head, router, useForm } from "@inertiajs/react"
import { ArrowLeft } from "@untitledui/icons"
import { useRef, useState } from "react"

import {
  type AdminOnwards,
  AdminOnwardsFlyover,
} from "@/components/admin/playlists/admin-onwards-flyover"
import { Breadcrumbs } from "@/components/ui/breadcrumbs"
import { Button } from "@/components/ui/button"
import { CopyLinkButton } from "@/components/warm-starts/copy-link-button"
import PublicLayout from "@/layouts/public-layout"
import {
  adminWarmStartFieldNotePath,
  adminWarmStartPath,
  adminWarmStartsPath,
  publishWarmStartClaimPath,
} from "@/routes"
import type {
  Playlist,
  Tag,
  WarmStartClaim,
  WarmStartCoordinator,
} from "@/types"

import { PublishCelebrateModal } from "../_components/publish-celebrate-modal"

import type { MarginNoteEditor, NoteAnchor } from "./_components/claim-margin"
import {
  ClaimTrackBoard,
  type ClaimTrackBoardHandle,
} from "./_components/claim-track-board"
import type {
  ClaimTrack,
  TrackPersistParams,
} from "./_components/claim-track-card"
import { OwnerEditManuscript } from "./_components/owner-edit-manuscript"
import PlaylistForm from "./_components/playlist-form"

interface EditPlaylistProps {
  playlist: Playlist
  claim?: WarmStartClaim | null
  /** The playlist's tracks, shipped only in claim mode for the track board. */
  tracks?: ClaimTrack[] | null
  /** Selectable system tags for the per-track edit slideout (claim mode only). */
  available_tags?: Tag[] | null
  just_published?: boolean
  /**
   * The coordinator (admin) edit surface payload. Its presence, with `claim`,
   * is the mode switch: the same editor, edits persisting to the draft via
   * ?warm_start=:id, no publish (that stays the recipient's moment).
   */
  coordinator?: WarmStartCoordinator | null
  /**
   * Admin-only payload. Present only when the page is reached via
   * Admin::PlaylistsController#edit, so the onward-placements flyover is
   * inherently gated to admins on that route.
   */
  adminOnwards?: AdminOnwards | null
}

export default function EditPlaylist({
  playlist,
  claim = null,
  tracks = null,
  available_tags = null,
  just_published = false,
  coordinator = null,
  adminOnwards = null,
}: EditPlaylistProps) {
  const [coverRemoved, setCoverRemoved] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const boardRef = useRef<ClaimTrackBoardHandle>(null)
  const [boardEmpty, setBoardEmpty] = useState(false)

  const mode: "normal" | "recipient" | "coordinator" = !claim
    ? "normal"
    : coordinator
      ? "coordinator"
      : "recipient"

  // How the board (and slideout) authorise and return their mutations.
  const persistParams: TrackPersistParams | null = claim
    ? coordinator
      ? { warm_start: coordinator.warm_start_id }
      : { claim: claim.token }
    : null

  // Derive the modal's open state from the prop rather than seeding it once.
  // Inertia reuses this component across the publish navigation, so a
  // `useState(just_published)` would keep its initial false and never open.
  const [celebrateDismissed, setCelebrateDismissed] = useState(false)
  const celebrateOpen = just_published && !celebrateDismissed

  const { data, setData, patch, processing, errors } = useForm({
    playlist: {
      title: playlist.title,
      description: playlist.description ?? "",
      cover_image: null as File | null,
      published: playlist.published ?? false,
    },
  })

  // --- Coordinator words autosave (commit-on-blur) ------------------------
  const lastSavedWords = useRef({
    title: playlist.title,
    description: playlist.description ?? "",
  })
  const [wordsStatus, setWordsStatus] = useState<"idle" | "saving" | "saved">(
    "idle",
  )
  // Words validation errors live in local state fed by the PATCH's onError —
  // not in the page error bag, which any later successful board visit would
  // wipe while the invalid words sat unsaved. Cleared on save or on revert.
  const [wordsErrors, setWordsErrors] = useState<
    Record<string, string | undefined>
  >({})
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const commitWords = () => {
    if (!coordinator) return
    const next = {
      title: data.playlist.title,
      description: data.playlist.description,
    }
    if (
      next.title.trim() === lastSavedWords.current.title.trim() &&
      next.description === lastSavedWords.current.description
    ) {
      // Back to the last-persisted words — nothing unsaved, nothing to flag.
      setWordsErrors({})
      return
    }

    setWordsStatus("saving")
    // Serialized through the board's queue: Inertia cancels a concurrent
    // visit, so a words PATCH racing a track mutation would drop one of them.
    const run = (done: () => void) =>
      router.patch(
        adminWarmStartPath(coordinator.warm_start_id),
        { playlist: next },
        {
          preserveScroll: true,
          preserveState: true,
          onSuccess: () => {
            lastSavedWords.current = next
            setWordsErrors({})
            setWordsStatus("saved")
            if (savedTimer.current) clearTimeout(savedTimer.current)
            savedTimer.current = setTimeout(() => setWordsStatus("idle"), 2000)
          },
          onError: (errs) => {
            setWordsErrors(
              errs as unknown as Record<string, string | undefined>,
            )
            setWordsStatus("idle")
          },
          onFinish: done,
        },
      )
    const board = boardRef.current
    if (board) board.enqueue(run)
    else run(() => undefined)
  }

  // --- Coordinator margin-note authoring -----------------------------------
  // One verb: PUT upserts the note for its anchor, an empty body deletes (the
  // DELETE variant is for the explicit trash affordance). Serialized through
  // the board's queue like the words save — Inertia cancels concurrent visits.
  // Notes re-render from the refreshed `claim.field_notes` prop; there is no
  // local cache to drift.
  const persistFieldNote = (anchor: NoteAnchor, body: string) => {
    if (!coordinator) return
    const run = (done: () => void) =>
      router.put(
        adminWarmStartFieldNotePath(coordinator.warm_start_id),
        { field_note: { ...anchor, body } },
        { preserveScroll: true, preserveState: true, onFinish: done },
      )
    const board = boardRef.current
    if (board) board.enqueue(run)
    else run(() => undefined)
  }

  const noteEditorFor =
    mode === "coordinator"
      ? (anchor: NoteAnchor): MarginNoteEditor => ({
          commit: (body: string) => persistFieldNote(anchor, body),
          remove: () => persistFieldNote(anchor, ""),
        })
      : undefined

  const playlistPath = `/playlists/${playlist.slug ?? playlist.id}`

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === "coordinator") return // words autosave; publish stays the recipient's

    // Claim mode: publishing is the action. The track board persists its edits
    // live through the normal track endpoints, so before flipping the playlist
    // public we flush any in-flight/queued track edits (and any pending remove),
    // then save the title/description tweaks and run the warm-start publish
    // pipeline. Cover and the visibility toggle aren't part of this path.
    if (claim) {
      setPublishing(true)
      await boardRef.current?.flush()
      router.patch(
        publishWarmStartClaimPath(claim.token),
        {
          playlist: {
            title: data.playlist.title,
            description: data.playlist.description,
          },
        },
        {
          preserveScroll: true,
          onFinish: () => setPublishing(false),
        },
      )
      return
    }

    if (data.playlist.cover_image instanceof File) {
      patch(playlistPath, {
        forceFormData: true,
      })
    } else if (coverRemoved) {
      patch(playlistPath, {
        forceFormData: true,
      })
    } else {
      // Strip cover_image from the form payload when no new file is being
      // uploaded — the server treats a null/File-less value as a deletion.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { cover_image, ...playlistData } = data.playlist
      const playlistDataWithoutCoverImage = { playlist: playlistData }
      router.patch(playlistPath, playlistDataWithoutCoverImage, {
        forceFormData: true,
      })
    }
  }

  return (
    <PublicLayout headerVariant="light">
      <Head
        title={
          mode === "coordinator" && coordinator
            ? `Draft for ${coordinator.recipient_name}`
            : claim
              ? `Publish ${playlist.title}`
              : `Edit ${playlist.title}`
        }
      />
      {mode === "coordinator" && coordinator && (
        <div className="border-brand bg-brand-primary/5 border-b">
          <div className="max-w-container mx-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5 lg:px-8">
            <p className="text-secondary text-sm">
              <span className="text-primary font-medium">
                {coordinator.recipient_name}&rsquo;s draft
              </span>{" "}
              &mdash; edits save as you make them; but only they can publish.
              <span className="text-tertiary" aria-live="polite">
                {wordsStatus === "saving" && " · Saving…"}
                {wordsStatus === "saved" && " · Saved"}
              </span>
            </p>
            <div className="flex shrink-0 items-center gap-2">
              {/* A revoked/expired draft stays editable, but its minted link
                  is dead on arrival — don't offer to copy it. */}
              {coordinator.claimable && (
                <CopyLinkButton url={coordinator.claim_url} withLabel />
              )}
              <Button
                type="button"
                size="sm"
                color="secondary"
                iconLeading={ArrowLeft}
                onClick={() => router.visit(adminWarmStartsPath())}
              >
                Back to warm starts
              </Button>
            </div>
          </div>
        </div>
      )}
      <section className="bg-secondary mb-3 pt-6 pb-8">
        <div className="max-w-container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          {!claim && (
            <div className="mb-3">
              <Breadcrumbs maxVisibleItems={3} />
            </div>
          )}
          {/* Page header. Coordinators get no special copy here: the top bar
              above already carries the draft context, and below it the page is
              exactly what the recipient sees. */}
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
              {claim ? "Make it yours" : "Edit Playlist"}
            </h1>
            <p className="text-md text-secondary">
              {claim
                ? "Arrange the tracks, make the words yours, then publish."
                : "Update your playlist details and cover image."}
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-container mx-auto mb-8 flex min-h-[calc(80vh)] flex-1 flex-col gap-5 px-4 lg:px-8">
        {mode === "normal" ? (
          <OwnerEditManuscript
            data={data.playlist}
            setData={(next) => setData("playlist", next)}
            errors={errors as Record<string, string | undefined>}
            processing={processing}
            initialDescription={data.playlist.description ?? ""}
            initialCoverUrl={playlist.cover_image_url ?? null}
            onSubmit={(e) => void submit(e)}
            onCancel={() => router.visit(playlistPath)}
            onCoverRemovedChange={setCoverRemoved}
            playlist={{ id: playlist.id, slug: playlist.slug ?? null }}
            tracks={tracks ?? []}
            availableTags={available_tags ?? []}
            returnMode={adminOnwards ? "admin_edit" : "edit"}
          />
        ) : (
          <PlaylistForm
            data={data.playlist}
            setData={(next) => setData("playlist", next)}
            errors={{
              ...(errors as Record<string, string | undefined>),
              ...wordsErrors,
            }}
            processing={claim ? publishing : processing}
            initialDescription={data.playlist.description ?? ""}
            initialCoverUrl={playlist.cover_image_url ?? null}
            coverErrorKey="playlist.cover_image"
            submitLabel={claim ? "Publish my playlist" : "Save Changes"}
            removeLabel="Change"
            onSubmit={(e) => void submit(e)}
            onCancel={() => router.visit(playlistPath)}
            onCoverRemovedChange={setCoverRemoved}
            claim={claim}
            submitDisabled={!!claim && boardEmpty}
            showFooter={mode !== "coordinator"}
            onWordsCommit={mode === "coordinator" ? commitWords : undefined}
            noteEditorFor={noteEditorFor}
            claimSlot={
              claim && tracks && persistParams ? (
                <ClaimTrackBoard
                  ref={boardRef}
                  playlist={{ id: playlist.id, slug: playlist.slug ?? null }}
                  claim={claim}
                  tracks={tracks}
                  availableTags={available_tags ?? []}
                  onEmptyChange={setBoardEmpty}
                  persistParams={persistParams}
                  noteEditorFor={noteEditorFor}
                />
              ) : undefined
            }
          />
        )}
      </main>

      {mode !== "coordinator" && (
        <PublishCelebrateModal
          open={celebrateOpen}
          onOpenChange={(next) => !next && setCelebrateDismissed(true)}
          playlistPath={playlistPath}
          showPasswordSetup={!!claim}
        />
      )}

      {adminOnwards && <AdminOnwardsFlyover {...adminOnwards} />}
    </PublicLayout>
  )
}
